import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, Badge } from "randmarcomps";
import { Info } from "lucide-react";
import type { PurchaseOrder as ApiPurchaseOrder } from "randmar-api-client";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import CancelPurchaseOrder from "../document/actions/CancelPurchaseOrder";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

interface IProps {
  purchaseOrders?: ApiPurchaseOrder[];
  defaultRowsPerPage?: number;
}

interface PurchaseOrderSummaryRow {
  purchaseOrderNumber: string;
  location?: string | null;
  purchaseOrderDate?: number;
  expectedArrivalDate?: number;
  vendorName?: string | null;
  subTotal: number;
  taxAmount1: number;
  taxAmount2: number;
  total: number;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

type PurchaseOrderFinancials = {
  SubTotal?: number | null;
  TaxAmount1?: number | null;
  TaxAmount2?: number | null;
  Total?: number | null;
};

const getApiPurchaseOrderFinancials = (purchaseOrder: ApiPurchaseOrder): PurchaseOrderFinancials | null => {
  const record = purchaseOrder as unknown as Record<string, unknown>;
  const subTotal = typeof record.SubTotal === "number" ? record.SubTotal : null;
  const taxAmount1 = typeof record.TaxAmount1 === "number" ? record.TaxAmount1 : null;
  const taxAmount2 = typeof record.TaxAmount2 === "number" ? record.TaxAmount2 : null;
  const total = typeof record.Total === "number" ? record.Total : null;

  if (subTotal === null && taxAmount1 === null && taxAmount2 === null && total === null) {
    return null;
  }

  return {
    SubTotal: subTotal,
    TaxAmount1: taxAmount1,
    TaxAmount2: taxAmount2,
    Total: total,
  };
};

const getPurchaseOrderLineSubTotal = (line: ApiPurchaseOrder): number => {
  return line.SubTotal ?? 0;
};

const pickRepresentativeApiPurchaseOrder = (lines: ApiPurchaseOrder[]): ApiPurchaseOrder => {
  return lines.reduce((best, current) => {
    const bestFinancials = getApiPurchaseOrderFinancials(best);
    const currentFinancials = getApiPurchaseOrderFinancials(current);
    const bestScore = Math.abs(bestFinancials?.Total ?? bestFinancials?.SubTotal ?? getPurchaseOrderLineSubTotal(best));
    const currentScore = Math.abs(
      currentFinancials?.Total ?? currentFinancials?.SubTotal ?? getPurchaseOrderLineSubTotal(current)
    );
    return currentScore > bestScore ? current : best;
  }, lines[0]);
};

const PurchaseOrdersGrid: React.FC<IProps> = ({ purchaseOrders, defaultRowsPerPage }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);

  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const summarizedRows = useMemo<PurchaseOrderSummaryRow[]>(() => {
    const groupedRows = new Map<string, ApiPurchaseOrder[]>();

    for (const line of purchaseOrders ?? []) {
      const purchaseOrderNumber = line.PurchaseOrderNumber ?? "";
      if (!purchaseOrderNumber) {
        continue;
      }

      const existing = groupedRows.get(purchaseOrderNumber);
      if (!existing) {
        groupedRows.set(purchaseOrderNumber, [line]);
      } else {
        existing.push(line);
      }
    }

    return Array.from(groupedRows.entries())
      .map(([purchaseOrderNumber, lines]) => {
        const representative = pickRepresentativeApiPurchaseOrder(lines);
        const financials = getApiPurchaseOrderFinancials(representative);
        const linesSubTotal = lines.reduce((sum, line) => sum + getPurchaseOrderLineSubTotal(line), 0);
        const subTotal = financials?.SubTotal ?? linesSubTotal;
        const taxAmount1 = financials?.TaxAmount1 ?? 0;
        const taxAmount2 = financials?.TaxAmount2 ?? 0;
        const total =
          financials?.Total ??
          (subTotal || taxAmount1 || taxAmount2 ? subTotal + taxAmount1 + taxAmount2 : 0);

        const purchaseOrderDate = lines.reduce((best, line) => Math.max(best, line.PurchaseOrderDate ?? 0), 0) || undefined;
        const expectedArrivalDateCandidate = lines.reduce(
          (best, line) => Math.min(best, line.ExpectedArrivalDate ?? Number.POSITIVE_INFINITY),
          Number.POSITIVE_INFINITY
        );
        const expectedArrivalDate =
          expectedArrivalDateCandidate === Number.POSITIVE_INFINITY ? undefined : expectedArrivalDateCandidate;
        const location =
          lines.map((line) => line.Location).find((value): value is string => Boolean(value)) ??
          representative.Location;
        const vendorName =
          lines.map((line) => line.VendorName).find((value): value is string => Boolean(value)) ??
          representative.VendorName;

        return {
          purchaseOrderNumber,
          location,
          purchaseOrderDate,
          expectedArrivalDate,
          vendorName,
          subTotal,
          taxAmount1,
          taxAmount2,
          total,
        };
      })
      .sort((a, b) => b.purchaseOrderNumber.localeCompare(a.purchaseOrderNumber));
  }, [purchaseOrders]);

  if (!summarizedRows.length) {
    return (
      <Alert className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Purchase orders</AlertTitle>
        <AlertDescription>
          Purchase Orders are how Randmar plans inbound inventory. There are no purchase orders to display right now.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section className="w-full">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Purchase orders</h3>
          <p className="text-[15px] text-slate-600">
            Aggregated totals across purchase order lines.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {summarizedRows.length} purchase order{summarizedRows.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO number
              </TableHead>
              <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Location
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO date
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Expected
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Subtotal
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 1
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 2
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Total
              </TableHead>
              <TableHead className="w-16 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summarizedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow
                  key={row.purchaseOrderNumber}
                  className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                >
                  <TableCell className="font-semibold">
                    <Link
                      to={Path.document(applicationId, row.purchaseOrderNumber)}
                      className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                    >
                      {row.purchaseOrderNumber}
                    </Link>
                    {row.vendorName && (
                      <p className="mt-1 text-xs text-slate-500">{row.vendorName}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700">{getWarehouseNameFromCode(row.location || undefined)}</TableCell>
                  <TableCell className="text-slate-700">{FormatDateFromInt(row.purchaseOrderDate)}</TableCell>
                  <TableCell className="text-slate-700">
                    {row.expectedArrivalDate === 0 ? (
                      <Badge variant="warning" size="sm">
                        To Be Provided
                      </Badge>
                    ) : (
                      FormatDateFromInt(row.expectedArrivalDate) || "-"
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.subTotal} />
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount1} />
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount2} />
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.total} />
                  </TableCell>
                  <TableCell className="text-right">
                    <CancelPurchaseOrder purchaseOrderNumber={row.purchaseOrderNumber} fab withReload />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {summarizedRows.length > rowsPerPage && (
        <TablePaginationBar
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={summarizedRows.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(nextRows) => setRowsPerPage(nextRows)}
        />
      )}
    </section>
  );
};

export default PurchaseOrdersGrid;

